/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import { compose } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import user from 'js/lib/user';

import { Grid, InlineNotification, PageGridContainer, useTheme } from '@coursera/cds-core';

import { closeMiniModalWithRoute, useRouterParams } from 'bundles/enterprise-legacy-xdp/components/ProgramMiniModal';
import { getProductPath, launchMiniModal } from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import type EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';
import type ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import ExperimentalProgramFullScreenModal from 'bundles/program-common/components/ExperimentalProgramFullScreenModal';
import type { Product, ProductType } from 'bundles/program-common/types/programCommon';
import ProgramHomeWrapper from 'bundles/program-home/components/ProgramHomeWrapper';
import { EnterpriseTargetSkillProfileSummariesQueryV1 } from 'bundles/program-home/utils/ProgramHomeGraphqlQueries';
import type {
  ObjectiveSummaryQuery_EnterpriseTargetSkillProfileSummariesV1Resource_bySlug_elements as ObjectiveSummary,
  ObjectiveSummaryQuery as ObjectiveSummaryQueryData,
  ObjectiveSummaryQueryVariables,
} from 'bundles/program-home/utils/__generated__/ObjectiveSummaryQuery';
import SkillPageContent, {
  SkillPageContentPlaceholder,
} from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/SkillPageContent';
import { isSkillIdEqual } from 'bundles/program-skills-objectives/utils';

import _t from 'i18n!nls/program-skills-objectives';

import ObjectivesBreadcrumb from '../ObjectivesBreadcrumb/ObjectivesBreadcrumb';

type TargetSkillProficiency = {
  skillId: string;
  skillName: string;
  targetProficiency: number;
  readableScore?: number;
};

type PropsFromRouter = {
  router: InjectedRouter;
  programSlug: string;
  objectiveSlug: string;
  skillId: string;
};

type PropsFromCaller = {
  userId: number;
  thirdPartyOrganizationId: string;
  programId: string;
  program: EnterpriseProgramsV1;
  thirdPartyOrganization: ThirdPartyOrganizationsV1;
  isProgramMember: boolean;
  isProgramAdmin: boolean;
  isBrowseOnlyProgram: boolean;
  shouldShowSponsoredByMessage: boolean;
  enableSkillsInSearchAndBrowse: boolean;
};

type PropsFromGraphql = {
  objectiveSummary?: ObjectiveSummary | null;
  targetSkillProficiency?: TargetSkillProficiency | null;
  loading: boolean;
  error: boolean;
};

export type Props = PropsFromCaller & PropsFromRouter & PropsFromGraphql;

// This page displays course recommendations for a given skill within a skillset.
export const ProgramSkillSetSkillRecommendationsPage: React.FC<Props> = ({
  objectiveSlug,
  programSlug,
  loading,
  error,
  objectiveSummary,
  targetSkillProficiency,
  thirdPartyOrganization,
  thirdPartyOrganizationId,
  programId,
  program,
  isProgramMember,
  isProgramAdmin,
  enableSkillsInSearchAndBrowse,
  router,
  isBrowseOnlyProgram,
  shouldShowSponsoredByMessage,
  skillId,
}) => {
  const theme = useTheme();
  const routerParams = useRouterParams(router);
  const onProductCardClick = (product: Product, productType: ProductType, collectionId?: string | null) => {
    launchMiniModal(router, product.id, productType === 'course', collectionId || '', undefined, undefined, {
      slug: product.slug,
      productPagePath: getProductPath(product),
    });
  };

  const programName = program?.metadata?.name ?? _t('My Learning Program');
  const userId = user.get().id;

  const styles = {
    page: css`
      background-color: var(--cds-color-white-0);
    `,
    container: css`
      margin: 0 auto;
    `,
    error: css`
      padding: ${theme.spacing(24, 48)};
      ${theme.breakpoints.down('sm')} {
        padding: ${theme.spacing(24, 12)};
      }
    `,
    backNav: css`
      margin: ${theme.spacing(24, 0, 0, 48)};
      ${theme.breakpoints.down('sm')} {
        margin-left: ${theme.spacing(12)};
      }
    `,
    back: css`
      margin-left: calc(${theme.spacing(8)} * -1);
    `,
  };

  return (
    <ProgramHomeWrapper
      className="rc-ProgramSkillSetSkillRecommendationsPage"
      thirdPartyOrganizationId={thirdPartyOrganizationId}
      thirdPartyOrganizationSlug={thirdPartyOrganization?.slug}
      program={program}
      programId={programId}
      programName={programName}
      isAuthenticatedUser={!!userId}
      css={styles.page}
      enableSkillsInSearchAndBrowse={enableSkillsInSearchAndBrowse}
    >
      {routerParams && (
        <ExperimentalProgramFullScreenModal
          thirdPartyOrganizationName={thirdPartyOrganization.name}
          isProgramMember={isProgramMember}
          isProgramAdmin={isProgramAdmin}
          programId={programId}
          renderCourseHomeLink
          onCloseMiniModal={() => closeMiniModalWithRoute(router)}
          isUpswell={false}
          hideAction={isBrowseOnlyProgram}
          disableSponsoredByMessage={!shouldShowSponsoredByMessage}
          {...routerParams}
          thirdPartyOrganizationSlug={thirdPartyOrganization.slug}
          program={program}
          breadcrumb={
            <ObjectivesBreadcrumb
              programSlug={programSlug}
              thirdPartyOrgSlug={thirdPartyOrganization.slug}
              thirdPartyOrganizationId={thirdPartyOrganization.id}
              programName={programName}
              skillName={targetSkillProficiency?.skillName}
              skillId={skillId}
              showSkillDashboard
            />
          }
        />
      )}
      {error && (
        <main css={[styles.container, styles.error]}>
          <InlineNotification severity="error">
            {_t('Sorry! Something went wrong. Please refresh the page.')}
          </InlineNotification>
        </main>
      )}
      {loading && (
        <main css={styles.container}>
          <PageGridContainer>
            <Grid item xs={12}>
              <SkillPageContentPlaceholder
                skillSetSlug={objectiveSlug}
                programSlug={programSlug}
                thirdPartyOrganization={thirdPartyOrganization}
                programName={programName}
              />
            </Grid>
          </PageGridContainer>
        </main>
      )}
      {objectiveSummary && targetSkillProficiency && (
        <main css={styles.container}>
          <PageGridContainer>
            <Grid item xs={12}>
              <SkillPageContent
                programId={programId}
                targetSkillProfileId={objectiveSummary.id}
                skillId={targetSkillProficiency.skillId}
                targetScore={targetSkillProficiency.targetProficiency}
                onProductCardClick={onProductCardClick}
                userId={userId}
                skillSetSlug={objectiveSlug}
                skillSetName={objectiveSummary.title}
                programSlug={programSlug}
                thirdPartyOrganization={thirdPartyOrganization}
                programName={programName}
              />
            </Grid>
          </PageGridContainer>
        </main>
      )}
    </ProgramHomeWrapper>
  );
};

const getTargetSkillProficiencyForSkillId = (
  objectiveSummary: ObjectiveSummary,
  skillId: string
): TargetSkillProficiency | null => {
  const targetSkillProficiency = objectiveSummary.targetSkillProficiencies.find((tsp) =>
    isSkillIdEqual(tsp.skillId, skillId)
  );

  return targetSkillProficiency
    ? {
        skillId,
        skillName: targetSkillProficiency.skillName,
        targetProficiency: targetSkillProficiency.targetProficiency,
      }
    : null;
};

const ProgramSkillSetSkillRecommendationsPageHookProxy = (props: Props) => (
  <ProgramSkillSetSkillRecommendationsPage {...props} />
);

export const enhance = compose<Props, PropsFromCaller>(
  connectToRouter<PropsFromRouter, {}>((router) => ({
    router,
    programSlug: router.params.programSlug,
    objectiveSlug: router.params.objectiveSlug,
    skillId: router.params.skillId,
  })),
  graphql<PropsFromRouter, ObjectiveSummaryQueryData, ObjectiveSummaryQueryVariables, PropsFromGraphql>(
    EnterpriseTargetSkillProfileSummariesQueryV1,
    {
      options: ({ objectiveSlug }) => ({
        variables: {
          objectiveSlug,
        },
      }),
      props: ({ data, ownProps }) => {
        const loading = data?.loading ?? true;
        const error = !!data?.error;
        const objectiveSummary = data?.EnterpriseTargetSkillProfileSummariesV1Resource?.bySlug?.elements[0];
        const targetSkillProficiency =
          !!ownProps?.skillId && objectiveSummary
            ? getTargetSkillProficiencyForSkillId(objectiveSummary, ownProps?.skillId)
            : null;
        return { loading, objectiveSummary, error, targetSkillProficiency };
      },
    }
  )
);

export default enhance(ProgramSkillSetSkillRecommendationsPageHookProxy);
